import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Calendar, Smartphone, Globe, Cpu, Database, Shield, Zap, Code, Palette, LucideIcon } from 'lucide-react';
import MobileLayout from '../layouts/MobileLayout';
import MobileHero from '../components/MobileHero';
import MobileSectionTitle from '../components/MobileSectionTitle';
import MobileCarousel from '../components/MobileCarousel';
import MobileServiceCardCarousel from '../components/MobileServiceCardCarousel';
import MobilePackCardCarousel from '../components/MobilePackCardCarousel';
import MobilePromoWheelPreview from '../components/MobilePromoWheelPreview';
import MobileAISection from '../components/MobileAISection';
import MobileAboutSection from '../components/MobileAboutSection';
import MobileFloatingChatIris from '../../components/mobile/MobileFloatingChatIris';
import MobileProjectSubmission from './MobileProjectSubmission';
import AppointmentBooking from '../../components/AppointmentBooking';
import { serviceApiService, Service } from '../../services/serviceApi';
import { packsApiService, Pack } from '../../services/packsApi';

// Map service names to icons
const getServiceIcon = (serviceName: string): LucideIcon => {
  const name = serviceName.toLowerCase();
  if (name.includes('mobile') || name.includes('app')) return Smartphone;
  if (name.includes('web') || name.includes('site')) return Globe;
  if (name.includes('ia') || name.includes('intelligence') || name.includes('ai')) return Cpu;
  if (name.includes('base') || name.includes('data')) return Database;
  if (name.includes('sécurité') || name.includes('security')) return Shield;
  if (name.includes('optimisation') || name.includes('performance')) return Zap;
  if (name.includes('design') || name.includes('ui')) return Palette;
  return Code; // Default icon
};

const MobileHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [showProjectSubmission, setShowProjectSubmission] = useState(false);
  const [showAppointmentBooking, setShowAppointmentBooking] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);

  // Load data from backend
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load services (active only)
      const servicesData = await serviceApiService.getServices(true);
      setServices(servicesData);
      
      // Load packs (active only)
      const packsData = await packsApiService.getPacks(true);
      setPacks(packsData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  if (showProjectSubmission) {
    return <MobileProjectSubmission onBack={() => setShowProjectSubmission(false)} />;
  }

  if (showAppointmentBooking) {
    return <AppointmentBooking onBack={() => setShowAppointmentBooking(false)} />;
  }

  return (
    <MobileLayout>
      {/* Hero Section */}
      <MobileHero
        onChatOpen={() => window.alert('Bientôt disponible')}
        onProjectSubmit={() => setShowProjectSubmission(true)}
        onAppointmentBook={() => setShowAppointmentBooking(true)}
      />

      {/* Roue de Promo Mobile - Preview */}
      <MobilePromoWheelPreview />

      {/* Services Section */}
      <section className="py-8 bg-white">
        <div className="px-4 mb-6">
          <MobileSectionTitle
            title="Nos Services"
            subtitle="Swipez pour découvrir nos solutions"
            centered
          />
        </div>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <>
            <MobileCarousel itemWidth={280} gap={16}>
              {services.slice(0, 6).map((service) => (
                <MobileServiceCardCarousel
                  key={service.id}
                  icon={getServiceIcon(service.name)}
                  title={service.name}
                  description={service.description}
                  onClick={() => navigate('/services')}
                />
              ))}
            </MobileCarousel>
            <div className="px-4 mt-6">
              <button
                onClick={() => navigate('/services')}
                className="w-full py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all"
              >
                Voir tous les services
              </button>
            </div>
          </>
        )}
      </section>

      {/* Packs Section */}
      <section className="py-8 bg-gradient-to-br from-gray-50 to-amber-50/20">
        <div className="px-4 mb-6">
          <MobileSectionTitle
            title="Nos Packs Premium"
            subtitle="Swipez pour comparer nos offres"
            centered
          />
        </div>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <>
            <MobileCarousel itemWidth={280} gap={16}>
              {packs.map((pack, index) => (
                <MobilePackCardCarousel
                  key={pack.id}
                  name={pack.name}
                  price={pack.total_price === 0 ? 'Sur devis' : `${pack.total_price.toLocaleString()}`}
                  features={[
                    pack.description,
                    `${pack.services.length} service${pack.services.length > 1 ? 's' : ''} inclus`,
                    ...(pack.services.slice(0, 3).map(s => s.name))
                  ]}
                  isPopular={index === 1}
                  onClick={() => navigate('/packs')}
                />
              ))}
            </MobileCarousel>
            <div className="px-4 mt-6">
              <button
                onClick={() => navigate('/packs')}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
              >
                Découvrir tous les packs
              </button>
            </div>
          </>
        )}
      </section>

      {/* AI Section */}
      <MobileAISection />

      {/* About Section */}
      <MobileAboutSection />

      {/* CTA Section */}
      <section className="px-4 py-10 bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-sm text-gray-300 mb-6">
            Contactez-nous pour discuter de vos besoins
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/contact')}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Nous contacter
            </button>
            <button
              onClick={() => setShowAppointmentBooking(true)}
              className="w-full py-3 bg-white/10 backdrop-blur-sm text-white rounded-xl font-medium border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              Prendre rendez-vous
            </button>
          </div>
        </div>
      </section>

      {/* Footer Simple */}
      <footer className="px-4 py-6 bg-white border-t border-gray-200">
        <div className="text-center">
          <h3 className="font-bold text-lg bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent mb-2">
            Iris Softech
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Solutions digitales sur mesure
          </p>
          <div className="flex justify-center gap-4 text-xs text-gray-500">
            <button onClick={() => navigate('/aboutirissoftech')}>À propos</button>
            <span>•</span>
            <button onClick={() => navigate('/contact')}>Contact</button>
            <span>•</span>
            <button onClick={() => navigate('/services')}>Services</button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            © 2024 Iris Softech. Tous droits réservés.
          </p>
        </div>
      </footer>

      {/* Floating Chat */}
      <MobileFloatingChatIris />
    </MobileLayout>
  );
};

export default MobileHomePage;
